import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  SERVICE_TYPE_LABELS,
  SERVICE_TYPES,
  formatINR,
} from "@garage/shared";
import { ApiError } from "../api/client";
import { useSaveService, useService } from "../api/services";
import {
  emptyItem,
  LineItems,
  lineAmount,
  type DraftItem,
} from "../components/LineItems";
import {
  Alert,
  Button,
  Card,
  Field,
  inputClass,
  Label,
  Spinner,
} from "../components/ui";
import { uploadAttachment } from "../lib/upload";

const today = () => new Date().toISOString().slice(0, 10);

export default function ServiceFormPage() {
  const { vehicleId = "", serviceId } = useParams();
  const editing = Boolean(serviceId);
  const navigate = useNavigate();
  const existing = useService(serviceId ?? "");
  const save = useSaveService(vehicleId, serviceId);

  const [form, setForm] = useState({
    type: "PERIODIC",
    title: "",
    serviceDate: today(),
    durationDays: "1",
    odometerKm: "",
    centreName: "",
    centreCity: "",
    labourCost: "",
    totalCost: "",
    nextDueDate: "",
    nextDueKm: "",
    notes: "",
  });
  const [items, setItems] = useState<DraftItem[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [totalTouched, setTotalTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [uploadNote, setUploadNote] = useState<string | null>(null);

  useEffect(() => {
    if (!editing || !existing.data) return;
    const s = existing.data.service;
    setForm({
      type: s.type,
      title: s.title,
      serviceDate: s.serviceDate,
      durationDays: String(s.durationDays),
      odometerKm: s.odometerKm?.toString() ?? "",
      centreName: s.centreName ?? "",
      centreCity: s.centreCity ?? "",
      labourCost: s.labourCost ?? "",
      totalCost: s.totalCost,
      nextDueDate: s.nextDueDate ?? "",
      nextDueKm: s.nextDueKm?.toString() ?? "",
      notes: s.notes ?? "",
    });
    setItems(
      s.items.map((i) => ({
        kind: i.kind,
        name: i.name,
        quantity: i.quantity,
        unitCost: i.unitCost ?? "",
        amount: i.amount ?? "",
        warrantyMonths: i.warrantyMonths?.toString() ?? "",
      })),
    );
    setTotalTouched(true);
  }, [editing, existing.data]);

  const partsTotal = items.reduce((sum, it) => sum + lineAmount(it), 0);
  const suggested = partsTotal + (Number(form.labourCost) || 0);

  // The suggestion follows the lines until the moment you type a total
  // yourself — after that it stops fighting you.
  useEffect(() => {
    if (!totalTouched && suggested > 0) {
      setForm((f) => ({ ...f, totalCost: suggested.toFixed(2) }));
    }
  }, [suggested, totalTouched]);

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setFields({});

    const body = {
      type: form.type,
      title: form.title,
      serviceDate: form.serviceDate,
      durationDays: form.durationDays || "0",
      totalCost: form.totalCost,
      ...(form.odometerKm ? { odometerKm: form.odometerKm } : {}),
      ...(form.centreName ? { centreName: form.centreName } : {}),
      ...(form.centreCity ? { centreCity: form.centreCity } : {}),
      ...(form.labourCost ? { labourCost: form.labourCost } : {}),
      ...(form.nextDueDate ? { nextDueDate: form.nextDueDate } : {}),
      ...(form.nextDueKm ? { nextDueKm: form.nextDueKm } : {}),
      ...(form.notes ? { notes: form.notes } : {}),
      items: items
        .filter((i) => i.name.trim())
        .map((i) => ({
          kind: i.kind,
          name: i.name,
          quantity: i.quantity || "1",
          ...(i.unitCost ? { unitCost: i.unitCost } : {}),
          ...(i.amount ? { amount: i.amount } : {}),
          ...(i.warrantyMonths ? { warrantyMonths: i.warrantyMonths } : {}),
        })),
    };

    try {
      const { service } = await save.mutateAsync(body);

      // Files are held until the service exists, because an attachment needs
      // a service id to hang off.
      if (files.length) {
        setUploadNote(`Uploading ${files.length} file(s)…`);
        for (const file of files) {
          try {
            await uploadAttachment(service.id, file);
          } catch (err) {
            setUploadNote(
              err instanceof ApiError && err.code === "STORAGE_UNCONFIGURED"
                ? "Service saved. Invoice uploads are not set up yet, so the file was skipped."
                : `Service saved, but ${file.name} could not be uploaded.`,
            );
            await new Promise((r) => setTimeout(r, 1800));
            break;
          }
        }
      }
      navigate(`/vehicles/${vehicleId}`, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setFields(err.fields ?? {});
        if (!err.fields) setError(err.message);
      } else {
        setError("Could not reach the server");
      }
    }
  }

  if (editing && existing.isPending) return <Spinner label="Loading service" />;

  return (
    <div className="max-w-3xl">
      <h1 className="text-2xl font-semibold tracking-tight">
        {editing ? "Edit service" : "Log a service"}
      </h1>

      <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-5">
        {error && <Alert>{error}</Alert>}

        <Card className="flex flex-col gap-4 p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="What kind" error={fields.type}>
              <select className={inputClass} value={form.type} onChange={set("type")}>
                {SERVICE_TYPES.map((t) => (
                  <option key={t} value={t}>{SERVICE_TYPE_LABELS[t]}</option>
                ))}
              </select>
            </Field>
            <Field label="Title" error={fields.title} hint="What you would call it later">
              <input className={inputClass} value={form.title} onChange={set("title")} placeholder="30,000 km service" required />
            </Field>
            <Field label="Date" error={fields.serviceDate}>
              <input className={inputClass} type="date" value={form.serviceDate} onChange={set("serviceDate")} required />
            </Field>
            <Field label="Days in workshop" error={fields.durationDays}>
              <input className={inputClass} type="number" min="0" value={form.durationDays} onChange={set("durationDays")} />
            </Field>
            <Field label="Odometer" error={fields.odometerKm} hint="Updates the vehicle's reading">
              <input className={inputClass} type="number" value={form.odometerKm} onChange={set("odometerKm")} placeholder="45000" />
            </Field>
            <Field label="Service centre">
              <input className={inputClass} value={form.centreName} onChange={set("centreName")} placeholder="Popular Maruti" />
            </Field>
          </div>
        </Card>

        <Card className="flex flex-col gap-4 p-5">
          <div>
            <h2 className="font-mono text-[11px] tracking-[0.12em] text-ink-3 uppercase">
              What was done
            </h2>
            <p className="mt-1 text-sm text-ink-3">
              Optional. Itemise it and the dashboard can tell you what you spend
              most on.
            </p>
          </div>
          <LineItems items={items} onChange={setItems} />
        </Card>

        <Card className="flex flex-col gap-4 p-5">
          <h2 className="font-mono text-[11px] tracking-[0.12em] text-ink-3 uppercase">
            Cost
          </h2>
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <Label>Parts (from lines)</Label>
              <p className="mt-1 py-2.5 tabular-nums">{formatINR(partsTotal)}</p>
            </div>
            <Field label="Labour" error={fields.labourCost}>
              <input className={inputClass} value={form.labourCost} onChange={set("labourCost")} inputMode="decimal" placeholder="1500" />
            </Field>
            <Field
              label="Total paid"
              error={fields.totalCost}
              hint={
                totalTouched && suggested > 0 && Number(form.totalCost) !== suggested
                  ? `Lines add up to ${formatINR(suggested)}`
                  : "This is what the report uses"
              }
            >
              <input
                className={inputClass}
                value={form.totalCost}
                onChange={(e) => {
                  setTotalTouched(true);
                  setForm((f) => ({ ...f, totalCost: e.target.value }));
                }}
                inputMode="decimal"
                placeholder="8750.50"
                required
              />
            </Field>
          </div>
        </Card>

        <Card className="flex flex-col gap-4 p-5">
          <h2 className="font-mono text-[11px] tracking-[0.12em] text-ink-3 uppercase">
            Invoice &amp; notes
          </h2>

          <Field label="Invoice copy" hint="PDF or photo, under 10 MB. Photos are compressed before upload.">
            <input
              className={`${inputClass} file:mr-3 file:rounded file:border-0 file:bg-line file:px-3 file:py-1 file:text-ink`}
              type="file"
              accept="application/pdf,image/*"
              multiple
              onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
            />
          </Field>
          {uploadNote && <p className="text-sm text-signal">{uploadNote}</p>}

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Next due date">
              <input className={inputClass} type="date" value={form.nextDueDate} onChange={set("nextDueDate")} />
            </Field>
            <Field label="Next due km">
              <input className={inputClass} type="number" value={form.nextDueKm} onChange={set("nextDueKm")} placeholder="55000" />
            </Field>
          </div>

          <Field label="Notes">
            <textarea className={inputClass} rows={3} value={form.notes} onChange={set("notes")} placeholder="Rattle from the front left was the anti-roll bar link." />
          </Field>
        </Card>

        <div className="flex gap-2">
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? "Saving…" : editing ? "Save changes" : "Log service"}
          </Button>
          <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
